import Controller from 'controllers/controller';
import EventManager from 'tools/event-manager';
import Bus from 'tools/bus';
import Elements from 'tools/elements';

class Breakpoint extends Controller {
    /**
     * Useful tool for reliably determining the current breakoint. This is an issue due to the differing ways in which
     * browsers consider the scrollbars in width/height calculations. By checking the visibility of a series of elements
     * set to show at different breakpoints, we can - in a cross browser way - correctly determine what the current
     * breakpoint is without checking window size.
     */
    constructor(app) {
        super(app);

        this.elements = new Elements;
        this.events = new EventManager({
            bind: this
        });
        this.bus = new Bus(this);
        this.breakpoint = null;

        this.breakpointToNumberMap = { };
        this.breakpoints = [ ];

        this.cacheElements();
        this.cacheBreakpoints();
        this.attachEvents();
        this.updateCurrentBreakpoint();
    }

    cacheElements() {
        this.elements.queryAll('breakpoints', '.bootstrap-breakpoint-detect');
    }

    cacheBreakpoints() {
        this.breakpointToNumberMap = {};

        this.elements.each('breakpoints', breakpoint => {
            let number = +breakpoint.getAttribute('data-number'),
                name = breakpoint.getAttribute('data-name');

            this.breakpointToNumberMap[name] = number;
        });
    }

    init() {
        this.cacheElements();
        this.cacheBreakpoints();
        this.updateCurrentBreakpoint();
    }

    attachEvents() {
        this.events.on(window, 'load', this.init);
        this.events.on(window, 'load', this.updateCurrentBreakpoint);
        this.events.on(window, 'resize', this.updateCurrentBreakpoint);
    }

    /**
     * Update the current breakpoint
     */
    updateCurrentBreakpoint() {
        let previous = this.breakpoint;

        this.elements.each('breakpoints', breakpoint => {
            let style = window.getComputedStyle(breakpoint);

            if (style.display !== 'none') {
                this.breakpoint = breakpoint.getAttribute('data-name');
            }
        });

        document.body.setAttribute('data-breakpoint', this.breakpoint);

        if (previous !== this.breakpoint) {
            this.bus.emit('change', this.breakpoint);
        }
    }

    /**
     * Retrive current breakpoint.
     *
     * @returns {null|*}
     */
    get() {
        return this.breakpoint;
    }

    /**
     * Retrieve the number representation of the breakpoint.
     *
     * @param breakpoint
     * @returns {*}
     */
    number(breakpoint = null) {
        if (breakpoint === null) {
            breakpoint = this.breakpoint;
        }

        return this.breakpointToNumberMap[breakpoint];
    }

    /**
     * Alias for .eq
     *
     * Check if given breakpoint is equal to current breakpoint.
     *
     * @param breakpoint
     * @returns {*}
     */
    is(breakpoint) {
        return this.eq(breakpoint);
    }

    /**
     * Check if given breakpoint is equal to current breakpoint.
     *
     * @param breakpoint
     * @returns {*}
     */
    eq(breakpoint) {
        return this.breakpoint === breakpoint;
    }

    /**
     * Check if given breakpoint is greater than current breakpoint.
     *
     * @param breakpoint
     * @returns {*}
     */
    gt(breakpoint) {
        return this.number() > this.number(breakpoint);
    }

    /**
     * Check if given breakpoint is less than current breakpoint.
     *
     * @param breakpoint
     * @returns {*}
     */
    lt(breakpoint) {
        return this.number() < this.number(breakpoint);
    }

    /**
     * Check if given breakpoint is less than or equal to current breakpoint.
     *
     * @param breakpoint
     * @returns {*}
     */
    lte(breakpoint) {
        return this.lt(breakpoint) || this.eq(breakpoint);
    }

    /**
     * Check if given breakpoint is greater than or equal to current breakpoint.
     *
     * @param breakpoint
     * @returns {*}
     */
    gte(breakpoint) {
        return this.gt(breakpoint) || this.eq(breakpoint);
    }
}

export default Breakpoint;